import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import './VideoSettings.css';
import { Select, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import ColorPicker from './ColorPicker';
import { CardFramingSettings, DEFAULT_CARD_FRAMING } from '../utils/cardFraming';
import { useProject } from '../contexts/ProjectContext';
import { trackEvent } from '../statsig';
import { FontSettingsMap } from '../utils/fontHelpers';

// Render format types - video formats call /new-render, image formats call /export-png
export type VideoFormat = 
  | 'H264' | 'H265' 
  | 'ProRes_422_Proxy' | 'ProRes_422_LT' | 'ProRes_422' | 'ProRes_422_HQ' 
  | 'ProRes_4444' | 'ProRes_4444_XQ';

export type ImageFormat = 'PDF' | 'PNG' | 'TIFF';

export type RenderFormat = VideoFormat | ImageFormat;

export const VIDEO_FORMATS: VideoFormat[] = [
  'H264', 'H265', 
  'ProRes_422_Proxy', 'ProRes_422_LT', 'ProRes_422', 'ProRes_422_HQ', 
  'ProRes_4444', 'ProRes_4444_XQ'
];

export const IMAGE_FORMATS: ImageFormat[] = ['PDF', 'PNG', 'TIFF'];

export function isVideoFormat(format: RenderFormat): format is VideoFormat {
  return VIDEO_FORMATS.includes(format as VideoFormat);
}

export function isImageFormat(format: RenderFormat): format is ImageFormat {
  return IMAGE_FORMATS.includes(format as ImageFormat);
}

export type VideoMode = "roller" | "cards";

export interface VideoSettings {
  background_color: string;
  fps_num: number;
  resolution: string;
  transparent: boolean;
  video_time: number;
  aspect_ratio: string;
  video_format: RenderFormat;
  fonts?: FontSettingsMap;
  logos?: Record<string, { url: string; isEnabled: boolean }>;
  mode: "roller" | "cards";
  card_framing?: CardFramingSettings;
}

export interface VideoDimensions {
  width: number;
  height: number;
}

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  background_color: '#000000',
  fps_num: 30,
  resolution: "HD",
  transparent: false,
  video_time: 60,
  aspect_ratio: '16:9',
  video_format: 'H264',
  fonts: {},
  logos: {},
  mode: "roller",
  card_framing: DEFAULT_CARD_FRAMING,
};

export const VIDEO_SIZES: Record<string, Record<string, VideoDimensions>> = {
  "HD": {
    "4:3": { width: 1440, height: 1080 },
    "16:9": { width: 1920, height: 1080 },
    "37:20": { width: 1920, height: 1038 },
    "2:1": { width: 1920, height: 960 },
    "239:100": { width: 1920, height: 804 },
  },
  "2K": {
    "4:3": { width: 1440, height: 1080 },
    "16:9": { width: 1920, height: 1080 },
    "37:20": { width: 1998, height: 1080 },
    "2:1": { width: 2048, height: 1024 },
    "239:100": { width: 2048, height: 858 },
  },
  "UHD": {
    "4:3": { width: 2880, height: 2160 },
    "16:9": { width: 3840, height: 2160 },
    "37:20": { width: 3840, height: 2076 },
    "2:1": { width: 3840, height: 1920 },
    "239:100": { width: 3840, height: 1606 },
  },
  "4K": {
    "4:3": { width: 2880, height: 2160 },
    "16:9": { width: 3840, height: 2160 },
    "37:20": { width: 3996, height: 2160 },
    "2:1": { width: 4096, height: 2048 },
    "239:100": { width: 4096, height: 1716 },
  },
};

export function calculateVideoDimensions(settings: Pick<VideoSettings, 'resolution' | 'aspect_ratio'>): VideoDimensions {
  return VIDEO_SIZES[settings.resolution]?.[settings.aspect_ratio] ?? VIDEO_SIZES.HD["16:9"];
}

export function calculateVideoHeight(settings: Pick<VideoSettings, 'resolution' | 'aspect_ratio'>): number {
  return calculateVideoDimensions(settings).height;
}

export function calculatePixelsPerFrame(
  settings: Pick<VideoSettings, 'resolution' | 'aspect_ratio' | 'fps_num'> & {
    video_time?: number;
    video_time_frames?: number;
  }
): number | null {
  const { video_time, video_time_frames, fps_num } = settings;
  if (!fps_num || fps_num <= 0) return null;
  const totalFrames: number = video_time_frames ?? (video_time ? video_time * fps_num : 0);
  if (!Number.isFinite(totalFrames) || totalFrames <= 0) return null;
  const height: number = calculateVideoHeight(settings);
  return Number((height / totalFrames).toFixed(2));
}

type VideoTimeMode = 'total' | 'pixels';
const DEFAULT_VIDEO_TIME_MODE: VideoTimeMode = 'pixels';

function pixelsPerFrameToSeconds(
  pixelsPerFrame: number,
  settings: Pick<VideoSettings, 'resolution' | 'aspect_ratio' | 'fps_num'>
): number {
  const height: number = calculateVideoHeight(settings);
  return height > 0 ? Number((height / (pixelsPerFrame * settings.fps_num)).toFixed(2)) : 0;
}

function toDisplayVideoTime(settings: VideoSettings, mode: VideoTimeMode): string {
  if (mode === 'pixels') {
    if (!settings.video_time || settings.video_time <= 0) return "";
    const pxPerFrame: number | null = calculatePixelsPerFrame(settings);
    return pxPerFrame !== null ? pxPerFrame.toString() : "";
  }

  return settings.video_time ? settings.video_time.toString() : "";
}

interface VideoSettingsPanelProps {
  settings: VideoSettings;
  onSettingsChange: (settings: VideoSettings) => void | Promise<void>;
  showTitle?: boolean;
  showBackgroundControls?: boolean;
}

export const VideoSettingsPanel: React.FC<VideoSettingsPanelProps> = ({ 
  settings, 
  onSettingsChange,
  showTitle = true,
  showBackgroundControls = true,
}) => {
  const { selectedProject } = useProject();
  const [timeMode, setTimeMode] = useState<VideoTimeMode>(DEFAULT_VIDEO_TIME_MODE);
  const [videoTimeInput, setVideoTimeInput] = useState(() => 
    toDisplayVideoTime(settings, DEFAULT_VIDEO_TIME_MODE)
  );
  const isEditingRef = useRef<boolean>(false);

  // Sync input with external settings when not actively editing.
  useEffect(() => {
    if (isEditingRef.current) {
      return;
    }
    const nextDisplay: string = toDisplayVideoTime(settings, timeMode);
    setVideoTimeInput(nextDisplay);
  }, [settings, timeMode]);

  const handleSettingsChange = (updates: Partial<VideoSettings>) => {
    const newSettings = { ...settings, ...updates };
    Object.keys(updates).forEach((field: string) => {
      trackEvent('project_setting_changed', {
        project_id: selectedProject?.id,
        settings_section: 'video_settings',
        property_name: field,
        video_mode: newSettings.mode,
      }, {
        dedupeKey: `${selectedProject?.id ?? 'no_project'}:video_settings:${field}`,
      });
    });
    const result = onSettingsChange(newSettings);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onSettingsChange:', error);
      });
    }
  };

  const handleModeChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ mode: e.target.value as VideoMode });
  }
  const handleFpsChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ fps_num: parseInt(e.target.value, 10) });
  };

  const handleResolutionChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ resolution: e.target.value });
  };

  const handleAspectRatioChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ aspect_ratio: e.target.value });
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setVideoTimeInput(raw);

    if (raw === "" || raw === null) {
      handleSettingsChange({ video_time: 0 });
      return;
    }

    const value = parseFloat(raw);
    if (isNaN(value) || value <= 0) {
      handleSettingsChange({ video_time: 0 });
      return;
    }

    if (timeMode === 'pixels') {
      const seconds = pixelsPerFrameToSeconds(value, settings);
      handleSettingsChange({ video_time: seconds });
    } else {
      handleSettingsChange({ video_time: value });
    }
  };

  const handleTransparentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSettingsChange({ transparent: e.target.checked });
  };

  const handleTimeModeChange = (e: SelectChangeEvent<string>) => {
    const newMode = e.target.value as VideoTimeMode;
    setTimeMode(newMode);
    trackEvent('project_setting_changed', {
      project_id: selectedProject?.id,
      settings_section: 'video_settings',
      property_name: 'video_time_mode',
      video_time_mode: newMode,
    });
    const nextDisplay = toDisplayVideoTime(settings, newMode);
    setVideoTimeInput(nextDisplay);
  };


  return (
    <div className="video-settings-panel">
      {showTitle ? <h2>{t`Video Settings`}</h2> : null}

      <div className="video-settings-group">
        {/* Video mode (roller/card) */}
        <div className="setting-item">
          <label>{t`Mode`}</label>
          <div className="input-wrapper">
            <Select
              value={settings.mode}
              onChange={handleModeChange}
              className="video-setting-select video-setting-select--fps"
              style={{ fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="roller">{t`Roller`}</MenuItem>
              <MenuItem value="cards">{t`Cards`}</MenuItem>
            </Select>
          </div>
        </div>

        {showBackgroundControls && (
          <div className="setting-item">
            <label>{t`Background Color`}</label>
            <div className="input-wrapper">
              {settings.transparent ? (
                <div
                  className="transparent-swatch"
                  title="Transparent background"
                >
                  T
                </div>
              ) : (
                <ColorPicker
                  value={settings.background_color}
                  onChange={(color) => handleSettingsChange({ background_color: color })}
                />
              )}
              <label>
                {t`Transparent`}:
                <input type="checkbox" checked={settings.transparent} onChange={handleTransparentChange} />
              </label>
            </div>
          </div>
        )}

        {/* FPS */}
        <div className="setting-item">
          <label>{t`FPS`}</label>
          <div className="input-wrapper">
            <Select
              value={settings.fps_num.toString()}
              onChange={handleFpsChange}
              className="video-setting-select video-setting-select--fps"
              style={{ fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="24">24</MenuItem>
              {settings.mode === 'cards' && (
                <MenuItem value="25">25</MenuItem>
              )}
              <MenuItem value="30">30</MenuItem>
              <MenuItem value="60">60</MenuItem>
              <MenuItem value="90">90</MenuItem>
              <MenuItem value="120">120</MenuItem>
            </Select>
          </div>
        </div>

        {/* Resolution */}
        <div className="setting-item">
          <label>
            {t`Resolution`}
          </label>
          <div className="input-wrapper">
            <Tooltip title={t`This setting determines the size of the video. Adjust font size accordingly.`} placement="top" arrow>
              <Select
                value={settings.resolution}
                onChange={handleResolutionChange}
                className="video-setting-select video-setting-select--resolution"
                style={{ fontSize: '16px' }}
                sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
              >
                <MenuItem value="HD">HD</MenuItem>
                <MenuItem value="2K">2K</MenuItem>
                <MenuItem value="UHD">UHD</MenuItem>
                <MenuItem value="4K">4K</MenuItem>
              </Select>
            </Tooltip>
          </div>
        </div>

        {/* Aspect Ratio */}
        <div className="setting-item">
          <label>
            {t`Aspect Ratio`}
          </label>
          <div className="input-wrapper">
            <Tooltip title={t`This setting greatly affects the video size.`} placement="top" arrow>
              <Select
                value={settings.aspect_ratio}
                onChange={handleAspectRatioChange}
                className="video-setting-select video-setting-select--aspect-ratio"
                style={{ fontSize: '16px' }}
                sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
              >
                <MenuItem value="4:3">4:3 / 1.33</MenuItem>
                <MenuItem value="16:9">16:9 / 1.78</MenuItem>
                <MenuItem value="37:20">37:20 / 1.85</MenuItem>
                <MenuItem value="2:1">2:1 / 2.00</MenuItem>
                <MenuItem value="239:100">239:100 / 2.39</MenuItem>
              </Select>
            </Tooltip>
          </div>
        </div>

        {/* Video Time */}
        { settings.mode === "roller" ? (
        <div className="setting-item">
          <label>
            {timeMode === 'total' ? t`Video Time (seconds)` : t`Pixels per frame`}
          </label>
          <div className="video-time-controls">
            <input
              id="time-setting"
              type="number"
              step="1"
              value={videoTimeInput}
              onChange={handleTimeChange}
              onFocus={() => { isEditingRef.current = true; }}
              onBlur={() => {
                isEditingRef.current = false;
                const raw = videoTimeInput.trim();
                const num = parseFloat(raw);
                if (isNaN(num) || num <= 0) {
                  handleSettingsChange({ video_time: 0 });
                  setVideoTimeInput("");
                  return;
                }

                if (timeMode === 'pixels') {
                  const seconds = pixelsPerFrameToSeconds(num, settings);
                  handleSettingsChange({ video_time: seconds });
                  setVideoTimeInput(Number(num.toFixed(2)).toString());
                } else {
                  handleSettingsChange({ video_time: num });
                  setVideoTimeInput(num.toString());
                }
              }}
              className={videoTimeInput === "" ? "input-empty video-time-input" : "video-time-input"}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select
                value={timeMode}
                onChange={handleTimeModeChange}
                className="video-setting-select video-setting-select--time-mode"
                style={{ fontSize: '14px' }}
                sx={{
                  '.MuiSelect-select': { textAlign: 'left', paddingLeft: '8px' },
                  '.MuiSelect-icon': { color: '#007CB2' }
                }}
                MenuProps={{ PaperProps: { style: { fontSize: 13 } } }}
              >
                <MenuItem value="total">{t`Total Time`}</MenuItem>
                <MenuItem value="pixels">{t`Pixels/frame`}</MenuItem>
              </Select>
            </div>
          </div>
        </div>
        ) : null}
      </div>
    </div>
  );
};
